import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { 
  Search as SearchIcon, 
  Filter, 
  Grid3X3,
  List,
  Award,
  Coffee,
  Briefcase,
  PartyPopper,
  Palette,
  Smartphone,
  Headphones,
  X,
  Compass
} from 'lucide-react';

import EventCard from '../components/EventCard';
import ImageSlider from '../components/ImageSlider';
import Footer from '../components/Footer';
import { eventsService } from '../services/eventsService';
import { Skeleton, EmptyState, Card, Button, Input } from '../components/ui';
import { useAuth } from '../contexts/AuthContext';
import { useAuthModal } from '../contexts/AuthModalContext';
import { favoritesService } from '../services/favoritesService';
import { useToast } from '../contexts/ToastContext';

interface Event {
  id: string;
  title: string;
  date: string;
  time: string;
  location: string;
  price: number;
  image: string;
  category: string;
  rating: number;
  attendees: number;
  tags: string[];
  distance?: string;
  promoted?: boolean;
}

const Search: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [priceRange, setPriceRange] = useState([0, 500]);
  const [selectedDate, setSelectedDate] = useState('all');
  const [sortBy, setSortBy] = useState('relevance');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [favorites, setFavorites] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [allEvents, setAllEvents] = useState<Event[]>([]);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const { isAuthenticated } = useAuth();
  const { openLogin } = useAuthModal();
  const { success, error: showError } = useToast();
  const eventsSectionRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Image slider data
  const sliderImages = [
    '/assets/55-650x360.png',
    'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&h=600&q=80',
    'https://images.unsplash.com/photo-1511735111819-9a3f7709049c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&h=600&q=80',
    'https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&h=600&q=80',
    'https://images.unsplash.com/photo-1540575467063-178a50c2df87?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&h=600&q=80',
    'https://images.unsplash.com/photo-1571266028243-d220c4a4a0b1?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&h=600&q=80'
  ];

  const categories = [
    { id: 'all', label: t('search.categories.all', 'All Events'), icon: Grid3X3, color: 'from-blue-400 to-yellow-500' },
    { id: 'music', label: t('search.categories.music', 'Music'), icon: Headphones, color: 'from-pink-400 to-red-500' },
    { id: 'sports', label: t('search.categories.sports', 'Sports'), icon: Award, color: 'from-green-400 to-emerald-500' },
    { id: 'business', label: t('search.categories.business', 'Business'), icon: Briefcase, color: 'from-indigo-400 to-blue-500' },
    { id: 'entertainment', label: t('search.categories.entertainment', 'Entertainment'), icon: PartyPopper, color: 'from-yellow-400 to-orange-500' },
    { id: 'arts', label: t('search.categories.arts', 'Arts & Culture'), icon: Palette, color: 'from-yellow-400 to-orange-500' },
    { id: 'food', label: t('search.categories.food', 'Food & Drink'), icon: Coffee, color: 'from-amber-400 to-yellow-500' },
    { id: 'tech', label: t('search.categories.tech', 'Technology'), icon: Smartphone, color: 'from-cyan-400 to-blue-500' }
  ];

  const locations = [
    { id: 'all', label: t('search.locations.all', 'All Locations') },
    { id: 'amman', label: t('search.locations.amman', 'Amman') },
    { id: 'irbid', label: t('search.locations.irbid', 'Irbid') },
    { id: 'zarqa', label: t('search.locations.zarqa', 'Zarqa') },
    { id: 'aqaba', label: t('search.locations.aqaba', 'Aqaba') },
    { id: 'karak', label: t('search.locations.karak', 'Karak') },
    { id: 'madaba', label: t('search.locations.madaba', 'Madaba') }
  ];

  const dateFilters = [
    { id: 'all', label: t('search.dates.all', 'Any Time') },
    { id: 'today', label: t('search.dates.today', 'Today') },
    { id: 'tomorrow', label: t('search.dates.tomorrow', 'Tomorrow') },
    { id: 'this_week', label: t('search.dates.this_week', 'This Week') },
    { id: 'this_weekend', label: t('search.dates.this_weekend', 'This Weekend') },
    { id: 'next_week', label: t('search.dates.next_week', 'Next Week') },
    { id: 'this_month', label: t('search.dates.this_month', 'This Month') }
  ];

  const sortOptions = [
    { id: 'relevance', label: t('search.sort.relevance', 'Most Relevant') },
    { id: 'date_asc', label: t('search.sort.date_asc', 'Date: Earliest First') },
    { id: 'date_desc', label: t('search.sort.date_desc', 'Date: Latest First') },
    { id: 'price_asc', label: t('search.sort.price_asc', 'Price: Low to High') },
    { id: 'price_desc', label: t('search.sort.price_desc', 'Price: High to Low') },
    { id: 'popularity', label: t('search.sort.popularity', 'Most Popular') },
    { id: 'rating', label: t('search.sort.rating', 'Highest Rated') }
  ];

  // Load events and favorites from service
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        // Force refresh events to ensure we have the latest calendar.jo data
        eventsService.refreshEventsWithNewData();
        const publishedEvents = await eventsService.getPublishedEvents();
        
        // Transform service events to Search page event format
        const transformedEvents: Event[] = publishedEvents.map(event => ({
          id: event.id,
          title: event.title,
          date: formatEventDate(event.date),
          time: event.time,
          location: event.venue,
          price: event.price,
          image: event.image,
          category: event.category.toLowerCase(),
          rating: 4.3 + Math.random() * 0.7, // Random rating between 4.3-5.0
          attendees: Math.floor(Math.random() * 25000) + 100,
          tags: event.tags || [],
          distance: `${(Math.random() * 10 + 1).toFixed(1)} km`,
          promoted: Math.random() > 0.7 // Random 30% chance of being promoted
        }));
        
        setAllEvents(transformedEvents);

        // Load user favorites if authenticated
        if (isAuthenticated) {
          try {
            const userFavorites = await favoritesService.getFavorites();
            setFavorites(userFavorites.map(fav => fav.id));
          } catch (err) {
            console.error('Error loading favorites:', err);
          }
        }
      } catch (error) {
        console.error('Error loading events:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [isAuthenticated]);

  // Handle sticky search bar
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsSticky(scrollPosition > 200);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Debounce search query
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Helper function to format date
  const formatEventDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        day: 'numeric',
        month: 'long', 
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  // Scroll to events section
  const scrollToEvents = useCallback(() => {
    eventsSectionRef.current?.scrollIntoView({ 
      behavior: 'smooth',
      block: 'start'
    });
  }, []);

  // Filter and search logic
  const filteredEvents = allEvents.filter(event => {
    const matchesSearch = debouncedSearchQuery === '' || 
      event.title.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ||
      event.location.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ||
      event.tags.some(tag => tag.toLowerCase().includes(debouncedSearchQuery.toLowerCase()));
    
    const matchesCategory = selectedCategory === 'all' || event.category === selectedCategory;
    const matchesLocation = selectedLocation === 'all' || event.location.toLowerCase().includes(selectedLocation);
    const matchesPrice = event.price >= priceRange[0] && event.price <= priceRange[1];
    
    return matchesSearch && matchesCategory && matchesLocation && matchesPrice;
  });

  // Sort events
  const sortedEvents = [...filteredEvents].sort((a, b) => {
    switch (sortBy) {
      case 'price_asc': return a.price - b.price;
      case 'price_desc': return b.price - a.price;
      case 'rating': return b.rating - a.rating;
      case 'popularity': return b.attendees - a.attendees;
      case 'date_asc': return new Date(a.date).getTime() - new Date(b.date).getTime();
      case 'date_desc': return new Date(b.date).getTime() - new Date(a.date).getTime();
      default: return 0;
    }
  });

  const handleFavorite = async (eventId: string) => {
    if (!isAuthenticated) {
      openLogin();
      return;
    }

    try {
      const isFavorited = favorites.includes(eventId);
      if (isFavorited) {
        await favoritesService.removeFavorite(eventId);
        setFavorites(prev => prev.filter(id => id !== eventId));
        success('Removed from favorites');
      } else {
        await favoritesService.addFavorite(eventId);
        setFavorites(prev => [...prev, eventId]);
        success('Added to favorites');
      }
    } catch (err: any) {
      showError(err.message || 'Failed to update favorite');
    }
  };

  const clearAllFilters = () => {
    setSearchQuery('');
    setSelectedCategory('all');
    setSelectedLocation('all');
    setPriceRange([0, 500]);
    setSelectedDate('all');
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (debouncedSearchQuery !== '') count++;
    if (selectedCategory !== 'all') count++;
    if (selectedLocation !== 'all') count++;
    if (priceRange[0] !== 0 || priceRange[1] !== 500) count++;
    if (selectedDate !== 'all') count++;
    return count;
  };

  const handleShare = async (eventId: string, eventTitle: string) => {
    const url = `${window.location.origin}/event/${eventId}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: eventTitle,
          text: `Check out this event: ${eventTitle}`,
          url: url
        });
      } catch (err) {
        // User cancelled or error occurred
      }
    } else {
      // Fallback: copy to clipboard
      try {
        await navigator.clipboard.writeText(url);
        success('Event link copied to clipboard!');
      } catch (err) {
        showError('Failed to copy link');
      }
    }
  };

  return (
    <div className="min-h-screen theme-transition relative overflow-hidden" style={{background: 'var(--liquid-gradient-bg)'}}>
      {/* Enhanced Liquid Glass Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-10 left-10 w-96 h-96 liquid-glass opacity-20 liquid-animate-morph"></div>
        <div className="absolute bottom-20 right-10 w-80 h-80 liquid-glass-strong opacity-15 liquid-animate-float" style={{animationDelay: '2s'}}></div>
        <div className="absolute top-1/2 left-1/3 w-64 h-64 liquid-glass opacity-10 liquid-animate-wave" style={{animationDelay: '4s'}}></div>
        <div className="absolute top-20 right-1/4 w-48 h-48 liquid-glass opacity-8 liquid-animate-pulse" style={{animationDelay: '6s'}}></div>
        <div className="absolute bottom-40 left-1/4 w-72 h-72 liquid-glass opacity-12 liquid-animate-morph" style={{animationDelay: '8s'}}></div>
      </div>

      {/* Hero Section with Enhanced Search and CTAs */}
      <section className="relative px-4 sm:px-6 lg:px-8 pt-20 sm:pt-24 pb-8 sm:pb-12" aria-label="Hero section">
        <div className="max-w-7xl mx-auto">
          {/* Hero Image Slider */}
          <div className="relative h-64 sm:h-80 md:h-96 lg:h-[500px] w-full rounded-2xl overflow-hidden mb-6 sm:mb-8" role="img" aria-label="Event showcase slider">
            <ImageSlider
              images={sliderImages}
              autoPlay={true}
              autoPlayInterval={4000}
              showDots={true}
              showArrows={true}
              className="w-full h-full"
            />
            
            {/* SFS Logo Overlay */}
            <div className="absolute inset-0 flex items-center justify-center z-10" aria-hidden="true">
              <img 
                src="/assets/SFS-logo.png" 
                alt="ShufeShow Logo" 
                className="w-48 h-48 sm:w-64 sm:h-64 md:w-80 md:h-80 lg:w-[416px] lg:h-[416px] object-contain drop-shadow-2xl"
                loading="eager"
              />
            </div>
            
            {/* Gradient Overlay for Better Logo Visibility */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/40 z-5" aria-hidden="true"></div>
          </div>

          {/* Prominent Search Bar */}
          <div className="relative z-20 mb-6 sm:mb-8">
            <div className={`glass-card-strong rounded-2xl sm:rounded-3xl p-4 sm:p-6 transition-all duration-300 ${isSearchFocused ? 'shadow-glass-strong scale-[1.01]' : 'shadow-glass'}`}>
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1 relative">
                  <Input
                    ref={searchInputRef}
                    type="text"
                    placeholder={t('home.searchPlaceholder', 'Search events by name or location')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => setIsSearchFocused(true)}
                    onBlur={() => setIsSearchFocused(false)}
                    icon={<SearchIcon size={20} className="text-white/70" />}
                    className="w-full text-lg sm:text-xl py-4 sm:py-5"
                  />
                </div>
                <div className="flex gap-3 sm:gap-4">
                  <Button
                    variant="glass"
                    size="lg"
                    onClick={() => setShowFilters(!showFilters)}
                    className="min-w-[120px] sm:min-w-[140px] min-h-[44px] sm:min-h-[48px]"
                    icon={<Filter size={20} />}
                    aria-label={`${t('search.filters', 'Filters')}${getActiveFiltersCount() > 0 ? ` (${getActiveFiltersCount()} active)` : ''}`}
                  >
                    {t('search.filters', 'Filters')}
                    {getActiveFiltersCount() > 0 && (
                      <span className="ml-2 px-2 py-0.5 bg-white/20 rounded-full text-xs font-bold" aria-hidden="true">
                        {getActiveFiltersCount()}
                      </span>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-stretch sm:items-center mb-8">
            <Button
              variant="glass"
              size="xl"
              onClick={scrollToEvents}
              className="flex-1 sm:flex-none sm:min-w-[200px] text-lg sm:text-xl py-4 sm:py-5 min-h-[56px] sm:min-h-[60px]"
              icon={<Compass size={24} />}
              aria-label={t('home.exploreEvents', 'Explore Events')}
            >
              {t('home.exploreEvents', 'Explore Events')}
            </Button>
          </div>
        </div>
      </section>

      {/* Sticky Search Bar (Mobile) */}
      {isSticky && (
        <div className="fixed top-16 sm:top-20 left-0 right-0 z-50 px-4 py-3 glass-card-strong border-b border-white/10 backdrop-blur-xl sm:hidden">
          <div className="flex gap-2">
            <Input
              type="text"
              placeholder={t('home.searchPlaceholder', 'Search events...')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              icon={<SearchIcon size={18} />}
              className="flex-1 text-base py-2.5"
            />
            <Button
              variant="glass"
              size="icon"
              onClick={() => setShowFilters(!showFilters)}
              className="min-w-[44px] min-h-[44px]"
            >
              <Filter size={20} />
              {getActiveFiltersCount() > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-yellow-400 rounded-full text-xs font-bold text-black flex items-center justify-center">
                  {getActiveFiltersCount()}
                </span>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Filters Panel */}
      {showFilters && (
        <div className="relative z-30 px-4 sm:px-6 lg:px-8 mb-6 sm:mb-8">
          <div className="max-w-7xl mx-auto">
            <Card variant="glass" className="p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h3 className="text-lg sm:text-xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>
                  {t('search.filters', 'Filters')}
                </h3>
                <div className="flex items-center gap-3">
                  {getActiveFiltersCount() > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearAllFilters}
                      className="text-sm"
                    >
                      {t('search.clearAll', 'Clear All')}
                    </Button>
                  )}
                  <button
                    onClick={() => setShowFilters(false)}
                    className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center glass-button rounded-xl hover:scale-110 transition-transform"
                    aria-label="Close filters"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>

              <div className="space-y-6">
                {/* Category Filter */}
                <div>
                  <label className="block text-sm font-medium mb-3" style={{color: 'var(--liquid-text-primary)'}}>
                    {t('search.category', 'Category')}
                  </label>
                  <div className="flex flex-wrap gap-2 sm:gap-3">
                    {categories.map((cat) => {
                      const IconComponent = cat.icon;
                      const isSelected = selectedCategory === cat.id;
                      return (
                        <button
                          key={cat.id}
                          onClick={() => setSelectedCategory(cat.id)}
                          className={`flex items-center gap-2 px-4 py-2 sm:px-5 sm:py-2.5 rounded-xl transition-all duration-300 min-h-[44px] ${
                            isSelected
                              ? 'glass-card-strong shadow-glass-strong scale-105'
                              : 'glass-button hover:scale-105'
                          }`}
                        >
                          <IconComponent size={18} className={isSelected ? 'text-yellow-400' : 'text-white/70'} />
                          <span className={`text-sm sm:text-base font-medium ${isSelected ? 'text-white' : 'text-white/80'}`}>
                            {cat.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Location Filter */}
                <div>
                  <label className="block text-sm font-medium mb-3" style={{color: 'var(--liquid-text-primary)'}}>
                    {t('search.location', 'Location')}
                  </label>
                  <select
                    value={selectedLocation}
                    onChange={(e) => setSelectedLocation(e.target.value)}
                    className="w-full px-4 py-3 glass-input rounded-xl text-white bg-white/10 border-white/20 focus:border-white/40 focus:ring-2 focus:ring-white/20 min-h-[44px]"
                  >
                    {locations.map((loc) => (
                      <option key={loc.id} value={loc.id} className="bg-gray-900">
                        {loc.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Date Filter */}
                <div>
                  <label className="block text-sm font-medium mb-3" style={{color: 'var(--liquid-text-primary)'}}>
                    {t('search.date', 'Date')}
                  </label>
                  <select
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="w-full px-4 py-3 glass-input rounded-xl text-white bg-white/10 border-white/20 focus:border-white/40 focus:ring-2 focus:ring-white/20 min-h-[44px]"
                  >
                    {dateFilters.map((date) => (
                      <option key={date.id} value={date.id} className="bg-gray-900">
                        {date.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Price Range */}
                <div>
                  <label className="block text-sm font-medium mb-3" style={{color: 'var(--liquid-text-primary)'}}>
                    {t('search.priceRange', 'Price Range')}: {priceRange[0]} - {priceRange[1]} JOD
                  </label>
                  <div className="flex items-center gap-4">
                    <input
                      type="range"
                      min="0"
                      max="500"
                      value={priceRange[0]}
                      onChange={(e) => setPriceRange([parseInt(e.target.value), priceRange[1]])}
                      className="flex-1 h-2 bg-white/20 rounded-lg appearance-none cursor-pointer accent-yellow-400"
                    />
                    <input
                      type="range"
                      min="0"
                      max="500"
                      value={priceRange[1]}
                      onChange={(e) => setPriceRange([priceRange[0], parseInt(e.target.value)])}
                      className="flex-1 h-2 bg-white/20 rounded-lg appearance-none cursor-pointer accent-yellow-400"
                    />
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* Events Section */}
      <section id="events-section" ref={eventsSectionRef} className="relative z-10 px-4 sm:px-6 lg:px-8 pb-20" aria-label="Events listing">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 sm:mb-8">
            <div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-2" style={{color: 'var(--liquid-text-primary)'}}>
                {t('search.events', 'Events')}
              </h2>
              <p className="text-white/70 text-sm sm:text-base" aria-live="polite" aria-atomic="true">
                {sortedEvents.length} {sortedEvents.length === 1 ? 'event' : 'events'} found
              </p>
            </div>
            
            <div className="flex items-center gap-3 w-full sm:w-auto">
              {/* Sort */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="flex-1 sm:flex-none px-4 py-2.5 glass-input rounded-xl text-white bg-white/10 border-white/20 focus:border-white/40 min-h-[44px] text-sm sm:text-base"
              >
                {sortOptions.map((option) => (
                  <option key={option.id} value={option.id} className="bg-gray-900">
                    {option.label}
                  </option>
                ))}
              </select>

              {/* View Toggle */}
              <div className="flex glass-button rounded-xl p-1 min-h-[44px]" role="group" aria-label="View mode">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`px-3 py-2 rounded-lg transition-all min-w-[44px] min-h-[44px] flex items-center justify-center ${
                    viewMode === 'grid' 
                      ? 'bg-white/20 text-white' 
                      : 'text-white/60 hover:text-white'
                  }`}
                  aria-label="Grid view"
                  aria-pressed={viewMode === 'grid'}
                >
                  <Grid3X3 size={20} />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-3 py-2 rounded-lg transition-all min-w-[44px] min-h-[44px] flex items-center justify-center ${
                    viewMode === 'list' 
                      ? 'bg-white/20 text-white' 
                      : 'text-white/60 hover:text-white'
                  }`}
                  aria-label="List view"
                  aria-pressed={viewMode === 'list'}
                >
                  <List size={20} />
                </button>
              </div>
            </div>
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className={`grid gap-6 ${
              viewMode === 'grid' 
                ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' 
                : 'grid-cols-1'
            }`}>
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Card key={i} variant="glass" className="p-6">
                  <Skeleton lines={3} height="200px" />
                </Card>
              ))}
            </div>
          )}

          {/* Events Grid/List */}
          {!isLoading && (
            <div className={`grid gap-6 ${
              viewMode === 'grid' 
                ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' 
                : 'grid-cols-1'
            }`}>
              {sortedEvents.map((event, index) => (
                <div
                  key={event.id}
                  className="animate-fade-in"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <EventCard 
                    event={event}
                    onFavorite={handleFavorite}
                    isFavorited={favorites.includes(event.id)}
                    onShare={() => handleShare(event.id, event.title)}
                    viewMode={viewMode}
                  />
                </div>
              ))}
            </div>
          )}

          {/* No Results */}
          {!isLoading && sortedEvents.length === 0 && (
            <EmptyState
              icon={SearchIcon}
              title={t('search.noResults', 'No events found')}
              description={t('search.noResultsDesc', 'Try adjusting your search criteria or filters to find more events.')}
              action={{
                label: t('search.clearFilters', 'Clear All Filters'),
                onClick: clearAllFilters
              }}
            />
          )}
        </div>
      </section>

      {/* Footer */}
      <Footer />
      
      {/* Skip to content link for accessibility */}
      <a 
        href="#events-section" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-yellow-400 focus:text-black focus:rounded-lg focus:font-bold"
        onClick={(e) => {
          e.preventDefault();
          eventsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }}
      >
        Skip to events
      </a>
    </div>
  );
};

export default Search;
